/**
 * FarmSmartly — In-memory API response cache
 *
 * Design:
 *  - Module-level singleton so it persists across renders / component mounts
 *  - Tag-based invalidation: every entry carries a set of tags;
 *    invalidateByTag(tag) wipes all matching entries at once
 *  - TTL: entries expire automatically; get() returns null for stale entries
 *  - Observers: React hooks subscribe to be notified when the cache changes
 *    so they can trigger a re-fetch
 *
 * Cache is cleared entirely:
 *  - On login   (new user session, stale data must not bleed through)
 *  - On logout
 *
 * Cache entries are invalidated by tag on every CUD operation (see AppContext).
 */

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes fallback
const CLEANUP_INTERVAL_MS = 30 * 1000; // 30 seconds - periodic cleanup interval

// ─── Environment-based TTL Configuration ─────────────────────────────────────

/**
 * Parse minutes from env var or return default (in milliseconds).
 * Vite env vars must be prefixed with VITE_ to be exposed to the client.
 */
function envTtlMinutes(varName, defaultMinutes) {
  const val = import.meta.env?.[varName];
  if (val && !isNaN(parseInt(val, 10))) {
    return parseInt(val, 10) * 60 * 1000;
  }
  return defaultMinutes * 60 * 1000;
}

// ─── Tag constants ────────────────────────────────────────────────────────────

export const CACHE_TAGS = {
  ROOMS:         'rooms',
  FLOCKS:        'flocks',
  EGGS:          'eggs',
  SALES:         'sales',
  EXPENSES:      'expenses',
  INCUBATOR:     'incubator',
  MORTALITY:     'mortality',
  FEED:          'feed',
  NOTIFICATIONS: 'notifications',
  EMPLOYEES:     'employees',
  ATTENDANCE:    'attendance',
  PAYROLL:       'payroll',
  REPORTS:       'reports',
  INVENTORY:     'inventory',
  MILK_STOCK:    'milkStock',
};

// ─── Module Cache Configuration ───────────────────────────────────────────────

/**
 * Module-level cache expiry configuration.
 * Each module can have its own TTL (time-to-live) in milliseconds.
 * Modules not listed here will use DEFAULT_TTL_MS.
 *
 * OPTIMIZED: Increased TTLs to reduce API calls while maintaining data freshness
 * - Reference data (rooms, flocks, employees): 15 minutes
 * - Transactional data (sales, expenses, eggs): 5 minutes
 * - Real-time data (notifications, attendance): 2 minutes
 */
export const MODULE_CACHE_CONFIG = {
  [CACHE_TAGS.ROOMS]:         { ttl: envTtlMinutes('VITE_CACHE_TTL_ROOMS', 15) },      // reference data, rarely changes
  [CACHE_TAGS.FLOCKS]:        { ttl: envTtlMinutes('VITE_CACHE_TTL_FLOCKS', 15) },      // reference data, rarely changes
  [CACHE_TAGS.EGGS]:          { ttl: envTtlMinutes('VITE_CACHE_TTL_EGGS', 5) },        // changes frequently
  [CACHE_TAGS.SALES]:         { ttl: envTtlMinutes('VITE_CACHE_TTL_SALES', 5) },       // changes frequently
  [CACHE_TAGS.EXPENSES]:      { ttl: envTtlMinutes('VITE_CACHE_TTL_EXPENSES', 5) },    // changes frequently
  [CACHE_TAGS.INCUBATOR]:     { ttl: envTtlMinutes('VITE_CACHE_TTL_INCUBATOR', 10) }, // semi-static
  [CACHE_TAGS.MORTALITY]:     { ttl: envTtlMinutes('VITE_CACHE_TTL_MORTALITY', 10) }, // changes occasionally
  [CACHE_TAGS.FEED]:          { ttl: envTtlMinutes('VITE_CACHE_TTL_FEED', 10) },      // changes occasionally
  [CACHE_TAGS.NOTIFICATIONS]: { ttl: envTtlMinutes('VITE_CACHE_TTL_NOTIFICATIONS', 2) }, // real-time
  [CACHE_TAGS.EMPLOYEES]:     { ttl: envTtlMinutes('VITE_CACHE_TTL_EMPLOYEES', 15) },  // reference data, rarely changes
  [CACHE_TAGS.ATTENDANCE]:   { ttl: envTtlMinutes('VITE_CACHE_TTL_ATTENDANCE', 2) },   // changes daily
  [CACHE_TAGS.PAYROLL]:      { ttl: envTtlMinutes('VITE_CACHE_TTL_PAYROLL', 15) },     // reference data
  [CACHE_TAGS.REPORTS]:      { ttl: envTtlMinutes('VITE_CACHE_TTL_REPORTS', 10) },   // semi-static
  [CACHE_TAGS.INVENTORY]:    { ttl: envTtlMinutes('VITE_CACHE_TTL_INVENTORY', 10) }, // changes occasionally
  [CACHE_TAGS.MILK_STOCK]:   { ttl: envTtlMinutes('VITE_CACHE_TTL_MILK_STOCK', 2) }, // reconciliation view, keep fresh
};

/**
 * Get the TTL for a given tag/module.
 * @param {string} tag - The cache tag/module
 * @returns {number} TTL in milliseconds
 */
export function getModuleTtl(tag) {
  return MODULE_CACHE_CONFIG[tag]?.ttl ?? DEFAULT_TTL_MS;
}

// ─── CacheStore ───────────────────────────────────────────────────────────────

class CacheStore {
  constructor() {
    this._store       = new Map();   // key → { data, tags, expiresAt }
    this._observers   = new Set();   // () => void  — notified on any change
    this._cleanupTimer = null;        // active cleanup interval timer

    // Start active cleanup to remove expired entries periodically
    this._startActiveCleanup();
  }

  // ── Active Cleanup ─────────────────────────────────────────────────────────

  /**
   * Start the active cleanup interval.
   * This runs even without API calls to remove expired entries.
   */
  _startActiveCleanup() {
    if (this._cleanupTimer) return; // Already running

    this._cleanupTimer = setInterval(() => {
      this._cleanupExpired();
    }, CLEANUP_INTERVAL_MS);

    // Ensure cleanup on page unload (best effort)
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this._stopActiveCleanup());
    }
  }

  /**
   * Stop the active cleanup interval.
   */
  _stopActiveCleanup() {
    if (this._cleanupTimer) {
      clearInterval(this._cleanupTimer);
      this._cleanupTimer = null;
    }
  }

  /**
   * Remove all expired entries from the cache.
   * This is called periodically by the active cleanup timer.
   */
  _cleanupExpired() {
    const now = Date.now();
    let changed = false;

    for (const [key, entry] of this._store) {
      if (now > entry.expiresAt) {
        this._store.delete(key);
        changed = true;
      }
    }

    if (changed) {
      this._notify();
    }
  }

  // ── Read ──────────────────────────────────────────────────────────────────

  /** Returns cached data or null if missing / expired. */
  get(key) {
    const entry = this._store.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this._store.delete(key);
      return null;
    }
    return entry.data;
  }

  has(key) {
    return this.get(key) !== null;
  }

  // ── Write ─────────────────────────────────────────────────────────────────

  /**
   * Store data under key.
   * @param {string}   key
   * @param {*}        data
   * @param {object}   [opts]
   * @param {number}   [opts.ttl]   TTL in ms (default 5 min)
   * @param {string[]} [opts.tags]  Invalidation tags
   */
  set(key, data, { ttl = DEFAULT_TTL_MS, tags = [] } = {}) {
    this._store.set(key, {
      data,
      tags:      Array.isArray(tags) ? tags : [tags],
      expiresAt: Date.now() + ttl,
    });
    this._notify();
  }

  // ── Invalidation ──────────────────────────────────────────────────────────

  /** Remove a single key. */
  invalidate(key) {
    if (this._store.has(key)) {
      this._store.delete(key);
      this._notify();
    }
  }

  /** Remove all entries that carry the given tag. */
  invalidateByTag(tag) {
    let changed = false;
    for (const [key, entry] of this._store) {
      if (entry.tags.includes(tag)) {
        this._store.delete(key);
        changed = true;
      }
    }
    if (changed) this._notify();
  }

  /** Remove all entries that carry ANY of the given tags. */
  invalidateByTags(tags) {
    let changed = false;
    for (const [key, entry] of this._store) {
      if (tags.some((t) => entry.tags.includes(t))) {
        this._store.delete(key);
        changed = true;
      }
    }
    if (changed) this._notify();
  }

  /** Wipe everything — call on login / logout. */
  clear() {
    if (this._store.size > 0) {
      this._store.clear();
      this._notify();
    }
  }

  // ── Observer (for React hooks) ────────────────────────────────────────────

  subscribe(fn) {
    this._observers.add(fn);
    return () => this._observers.delete(fn);   // returns unsubscribe
  }

  _notify() {
    for (const fn of this._observers) fn();
  }

  // ── Debug ─────────────────────────────────────────────────────────────────

  /** Returns a snapshot of current entries — useful for devtools. */
  getStats() {
    const now = Date.now();
    return [...this._store.entries()].map(([key, entry]) => ({
      key,
      tags:    entry.tags,
      ttlLeft: Math.max(0, Math.round((entry.expiresAt - now) / 1000)),
      stale:   now > entry.expiresAt,
    }));
  }

  /** Get summary statistics grouped by tag/module. */
  getModuleStats() {
    const now = Date.now();
    const stats = {};

    for (const [tag, config] of Object.entries(MODULE_CACHE_CONFIG)) {
      stats[tag] = { count: 0, expired: 0, ttl: config.ttl };
    }

    for (const [, entry] of this._store) {
      for (const tag of entry.tags) {
        if (!stats[tag]) {
          stats[tag] = { count: 0, expired: 0, ttl: getModuleTtl(tag) };
        }
        stats[tag].count++;
        if (now > entry.expiresAt) {
          stats[tag].expired++;
        }
      }
    }

    return stats;
  }

  get size() { return this._store.size; }

  // ── Public Cleanup ───────────────────────────────────────────────────────

  /**
   * Manually trigger cleanup of expired entries.
   * This can be called by components if needed.
   */
  cleanupExpired() {
    this._cleanupExpired();
  }
}

// Singleton exported for the whole app
export const cache = new CacheStore();
